/**
 * Section 12 — FNO / Options.
 *
 * Two pages, one section: the OptionsDashboard pulse-card grid and
 * the per-symbol OptionsLab with its 7 tabs. Covers basket building,
 * payoff curves, Greeks, OI walls, IV/skew, GEX, VWAP-OI, futures
 * tab, and the Strategy Suggestions flow (R67b).
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_fno_options: GuideSection = {
  id: 'fno-options',
  label: 'FNO / Options',
  group: 'pages',
  icon: <AccountTreeIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>FNO / Options</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The <strong>analytics</strong> half of options: <OpenInApp to="/options-dashboard" label="Options Dashboard" />
        (a.k.a. FNO Intelligence — the pulse-card grid scanning all underlyings) and the <strong>Options Lab</strong>{' '}
        (per-symbol deep dive with 7 tabs, opened by clicking a Dashboard card, route{' '}
        <code>/options-lab/&lt;symbol&gt;</code>). These now live <strong>under the unified
        <OpenInApp to="/options" label="Options hub" /></strong> — reach FNO Intelligence from the hub's
        "Scan the market" card (the standalone "FNO" left-nav item was retired; the routes stay live). For
        <em> building</em> and <em>monitoring</em> option trades, see the
        <OpenInApp to="/app-guide#options-at" label="Options — hub, builder & auto-trade" /> section.
      </Typography>

      <GuideCallout kind="warning" title="Option-chain polling is config-gated (off until you enable it)">
        The background option-chain poller <strong>idles until you turn it on</strong> in
        <strong> Settings → Options Polling</strong>. While it's off, the stored option-chain / Greeks / OI /
        GEX snapshots and the pulse-card grid <strong>do not auto-refresh</strong>, so the figures here can be
        stale or unavailable. Two notes: (1) the <strong>Charts</strong> page option features source the
        <strong> live Delta chain directly</strong>, so they work regardless of this poller; (2) the
        <OpenInApp to="/ai-desk?tab=engine" label="AI Desk → Engine" /> and <OpenInApp to="/option-strategies" label="Options AT" />
        engines use candle data, not this poller. Enable polling if you want the Dashboard/Lab figures below to
        stay live.
      </GuideCallout>

      <H id="dashboard">Options Dashboard — the pulse grid</H>
      <P>
        Grid of cards, one per underlying (NIFTY, BANKNIFTY, FINNIFTY, individual stocks). Each card
        summarises the option-positioning state on that underlying — high-conviction tilts get bigger /
        starred. Aimed at "which underlying should I look at next?".
      </P>
      <H3>Pulse score</H3>
      <P>
        Each card has a 0–100 score derived from:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="OI buildup vs unwinding ratio (more conviction = bigger imbalance)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="IV skew (where vol is being paid for; far OTM put skew = fear)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="GEX magnitude (dealer-hedging pressure)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Recent futures-vs-spot basis shifts (rolling cost-of-carry changes)." />
        </ListItem>
      </List>
      <P>
        Star a card to keep it in your "high-conviction" filter. Click any card → opens that underlying in
        the Options Lab.
      </P>
      <H3>Filters</H3>
      <P>
        Top of the page has filter chips: <strong>Star</strong> (saved high-conviction), <strong>Exchange</strong>{' '}
        (NFO / All), <strong>Direction</strong> (bullish / bearish / neutral cards), <strong>Search</strong>{' '}
        (text on symbol). Filters are AND-composed and persist between visits (localStorage).
      </P>
      <GuideCallout kind="tip">
        First-time users: enable the Star filter on day one so your daily routine is "open Dashboard →
        scan starred cards → drill into the one with the biggest score change since yesterday".
      </GuideCallout>

      <H id="lab">Options Lab — the per-symbol deep dive</H>
      <P>
        7 tabs in a fixed top-to-bottom order. Open by clicking any card on the Dashboard, or by going to
        <code> /options-lab/&lt;symbol&gt;</code> directly.
      </P>

      <H3>Tab 1 — Overview</H3>
      <P>
        The single-screen summary. Shows:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Pinned metrics strip</strong>: spot, futures (lead expiry), basis, ATM IV, GEX, max-pain. Each cell tinted red/green vs its 30-day baseline.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Why this signal? panel (collapsed)</strong> — top 3 factors contributing to the pulse score, with weights.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Suggested baskets</strong> — top 3 ranked option strategies the Strategy Suggestions engine picked for the current setup (more in R67b).</>} />
        </ListItem>
      </List>

      <H3>Tab 2 — OI (Open Interest)</H3>
      <P>
        OI wall map. X-axis = strikes; Y-axis = open interest in lots. Two bar series — Calls (red, OTM
        right of spot) and Puts (green, OTM left of spot).
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>OI buildup vs unwinding</strong> — colour intensity encodes whether OI grew or shrank vs yesterday. Bright = adding; pale = unwinding.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Max-pain marker</strong> — vertical line at the strike where total option-writer pain is minimised. Often acts as a magnet near expiry.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>PCR (Put/Call Ratio)</strong> — single number at the top of the panel. PCR &gt; 1 = more put OI than call OI = potentially bullish (puts get bought as protection / writers profit from premium decay).</>} />
        </ListItem>
      </List>
      <GuideCallout kind="info" title="Reading OI walls">
        A strike with huge open interest acts as a price magnet. If price approaches an OI wall, expect
        either: (a) it stops there (writers defend), or (b) it punches through with momentum (writers
        capitulate, often a fast move follows). Watch the strike directly.
      </GuideCallout>

      <H3>Tab 3 — IV / Skew</H3>
      <P>
        Implied volatility by strike for the chosen expiry. Two lines — calls IV and puts IV — should be
        close together at ATM, diverging towards the tails (the "smile" or "skew").
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="ATM IV value pinned at the top — the 'fair vol' market is currently pricing." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Skew</strong> = OTM-put IV minus OTM-call IV at equivalent moneyness. Positive skew = fear (downside protection demand); strong negative skew = greed.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Comparison line — yesterday's IV curve, for context on whether the smile has steepened or flattened." />
        </ListItem>
      </List>

      <H3>Tab 4 — GEX (Gamma Exposure)</H3>
      <P>
        Dealer-positioning aggregate. Sums gamma × OI across all strikes for one expiry. Positive GEX = dealers
        net-long gamma (their hedging dampens price moves). Negative GEX = dealers net-short gamma (their hedging
        amplifies moves).
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Total-GEX number at the top." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>GEX-by-strike</strong> bar chart. Strikes with the largest |GEX| are 'pinning levels' where dealer hedging is most active.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Zero-gamma flip</strong> — the spot level at which net GEX changes sign. Below it dealer hedging amplifies; above it dampens. Treat as a regime boundary.</>} />
        </ListItem>
      </List>

      <H3>Tab 5 — VWAP-OI (now labelled "Flow" in some places)</H3>
      <P>
        Where the option <em>flow</em> happened — volume-weighted average price of option trades by strike.
        Distinct from OI which is positioning state; Flow tells you the recent direction of activity.
      </P>
      <P>
        A strike with high call-buy flow + flat OI = aggressive buyers but writers absorbing. A strike with
        high call-sell flow + rising OI = aggressive option writers — bearish positioning.
      </P>

      <H3>Tab 6 — Chain</H3>
      <P>
        Full option chain table for the chosen expiry. Columns: Strike, Call (LTP, IV, OI, ΔOI, Volume,
        Delta, Gamma, Theta, Vega), Put (same columns mirrored). Tick rows to add legs to a custom basket
        (next tab).
      </P>

      <H3>Tab 7 — Futures</H3>
      <P>
        Current-month / next-month / far-month futures prices + basis to spot. Basis curve plotted over the
        last 30 days. Basis spike = roll-cost change worth investigating (often precedes a spot move).
      </P>

      <H id="basket">Strategy Suggestions + basket execution (R67b)</H>
      <P>
        On Tab 1 Overview, three top-ranked strategy baskets are pre-built for the current setup. Each
        basket shows:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Strategy name (Long Straddle / Iron Condor / Bull Call Spread / Bear Put Spread / Calendar / Diagonal / Synthetic Future)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Auto-picked legs (strike + side + qty) based on Delta target." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Payoff curve at expiry with breakeven, max-profit, max-loss markers." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Premium required + margin estimate." />
        </ListItem>
      </List>
      <P>
        Click <strong>Execute Basket</strong> → opens OrderConfirmDialog's Strategy tab pre-filled with the
        legs. Each leg fires as a separate broker order (one after the other); a per-leg fill chip
        confirms each as it goes through.
      </P>
      <GuideCallout kind="warning" title="Considered-but-dropped baskets (R67e)">
        Below the top 3, a collapsed "Considered but dropped" section lists baskets the engine evaluated
        and rejected, with the reason. Use this to learn what disqualifies a setup (e.g. "Skew too flat for
        Iron Condor", "Premium too low for Calendar").
      </GuideCallout>

      <H id="payoff-curve">Payoff curve — how to read it</H>
      <P>
        X-axis = underlying price at expiry. Y-axis = P&L (in INR) of the basket at that price. Key
        markers:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Breakeven</strong> — where the curve crosses zero. Could be one (bullish/bearish strategy) or two (range strategy like Iron Condor).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Max profit</strong> — the curve's peak. Marked with a green dot.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Max loss</strong> — curve's trough. Red dot. For uncapped strategies (naked options) this is "−∞" and the chart truncates at a sensible bound.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Current spot marker</strong> — vertical dashed line at the underlying's current price so you can see if you're already in/out-of-the-money.</>} />
        </ListItem>
      </List>
      <P>
        Hover any point on the curve for the exact P&L value. The curve is "at expiry" — actual P&L during
        the trade includes time-decay (theta) and vol-change (vega) effects not shown.
      </P>

      <H id="greeks-history">Greeks-over-time (C.1)</H>
      <P>
        Per-strike Greeks (Δ Γ Θ V) are snapshotted server-side every 5 minutes during market hours. A
        per-strike history chart is reachable from the Chain tab — click a row's Δ value → "View history"
        → modal opens with a line chart of that Greek over the last N days.
      </P>
      <P>
        Useful for diagnosing: "why did my position's Δ change so much overnight?" or "is gamma building
        into expiry?".
      </P>

      <H id="fno-chart">The FNO indicator on the chart — one expiry, candle by candle</H>
      <P>
        Turn on the <strong>FNO</strong> chip (BTC / ETH on Delta). The chart then shows <strong>one option expiry</strong>{' '}
        as it stood at the <strong>close of every candle</strong> — so you can scroll back and see what the options
        market looked like when each candle closed, and a closed candle never changes afterwards.
      </P>
      <H3>Choosing the expiry</H3>
      <P>
        Click the <strong>FNO chip</strong> on the chart legend (top-left, under the price line; every layout) or use the{' '}
        <strong>Expiry</strong> group in the toolbar: the <strong>D0 / W0 / M0</strong> chips, then every listed expiry by
        date with its time left (an <strong>M</strong> marks a monthly). The Options Workspace layout shows the dates as its
        own pill row instead:<br />
        • <strong>D0</strong> — the front expiry, whatever kind. <strong>W0</strong> — the front weekly (the month&apos;s
        last Friday counts as that week&apos;s weekly). <strong>M0</strong> — the front monthly. These <em>roll</em>: the
        moment the contract settles (12:00 UTC on Delta = 17:30 IST) the next one takes over, and the candle where that
        happened gets a small <strong>&ldquo;D0 → 04 Nov&rdquo;</strong> mark with a dotted line.<br />
        • <strong>A date</strong> (e.g. 27 Nov) <strong>pins</strong> that one contract: you see its own history and
        nothing else. The pin is remembered per coin in this browser, is highlighted orange with a 📌 in the toolbar
        (click it again, or ×, to go back to the rolling tier), and is dropped once that expiry has settled. In the <strong>Options Workspace</strong>, clicking an
        expiry pill pins it too; a blue underline marks the pill the chart&apos;s FNO data is showing.<br />
        The right-rail Option Profile follows the same expiry.
      </P>
      <H3>What is drawn, and the colours</H3>
      <P>
        One colour means one thing everywhere: <strong style={{ color: '#089981' }}>green = puts</strong> (writers&apos;
        floor, support) and <strong style={{ color: '#f23645' }}>red = calls</strong> (their ceiling, resistance).<br />
        • <strong>Call wall / Put wall</strong> — the strike with the most call / put open interest, per candle (more
        per side in Chart Settings). A light <strong>fill</strong> between them marks the range writers are defending.<br />
        • <strong>Max pain</strong> (amber, dashed) and the <strong>γ flip</strong> (purple, long dashes) — above the flip
        dealers damp moves, below it they amplify them.<br />
        • <strong>PCR pane</strong> — put/call OI ratio around 1.0: <strong>green above</strong> (more puts written —
        support below price), <strong>red below</strong> (more calls — a cap above), with an EMA.<br />
        • <strong>ATM IV pane</strong> — the expiry&apos;s at-the-money implied volatility; its label shows the change over
        the last 12 candles.<br />
        • <strong>OI change pane</strong> — open interest added on each candle, puts minus calls: <strong>green bars</strong>{' '}
        = more puts added (support building), <strong>red bars</strong> = more calls added (resistance building). Switch it
        to <em>Total calls vs puts</em> in Chart Settings.<br />
        • Each pane has a label with the hovered candle&apos;s value; the legend&apos;s FNO row lists call wall, put wall
        (with their distance from the close), max pain, γ flip, the γ regime (γ+ pinning / γ− trending), PCR, IV, the
        expected move (ATM straddle) and the OI change.<br />
        • Optional (Chart Settings → FNO): GEX walls, the expected-move band (spot ± straddle) and a γ-regime background
        tint (teal = long gamma, orange = short gamma). Every line and pane can be switched off there.
      </P>
      <H3>Gaps are shown as gaps</H3>
      <P>
        If no FNO record exists within 20 minutes of a candle, that candle is <strong>empty</strong>: the lines break and
        the candle is shaded grey (&ldquo;no FNO data — collector was down&rdquo;). Nothing is carried forward across an
        outage. The 28 Oct – 1 Nov 2026 hole is one of these: the analysis failed for 4½ days and that history cannot be
        rebuilt. Since 3 Nov every listed expiry is recorded, and the admins get a Telegram if the history stops again.
        Charts under 30 minutes use 5-minute records (last 10 days at most); 30 minutes and up use 30-minute records.
      </P>

      <H id="gex-walls">GEX walls on the chart — how to read &amp; trade them</H>
      <P>
        With the <strong>FNO</strong> chart indicator on, switch on <strong>GEX walls</strong> in Chart Settings → FNO
        to overlay the option <strong>Gamma-Exposure (GEX) walls</strong> next to the max-pain, OI-wall and γ-flip
        lines. GEX measures how much <em>dealer hedging</em> sits at each strike:
        <strong> GEX = Σ (γ<sub>call</sub>·OI − γ<sub>put</sub>·OI) · lot · spot² · 0.01</strong>.
        Dealers are assumed long calls / short puts, so call strikes add <strong>positive</strong> gamma
        and put strikes add <strong>negative</strong> gamma.
      </P>
      <H3>What the walls are</H3>
      <P>
        The chart marks the <strong>top ±3 strikes by |GEX|</strong> — the biggest hedging concentrations:
        <br />• <strong>Green wall (positive gamma)</strong> — dealers must SELL into rallies and BUY into
        dips to stay hedged. This <em>dampens</em> price: green walls act as <strong>magnets / pin levels</strong>{' '}
        and as soft support/resistance.<br />
        • <strong>Red wall (negative gamma)</strong> — dealers must BUY into rallies and SELL into dips,
        which <em>amplifies</em> price. Red walls are <strong>accelerant levels</strong>: once breached,
        moves tend to extend rather than fade.
      </P>
      <H3>What to do when a candle hits a wall</H3>
      <P>
        • <strong>Candle reaches a GREEN wall (and net gamma is positive):</strong> expect rejection /
        stall / pin — fade the move back toward the wall. This is the environment to <strong>sell premium</strong>{' '}
        (straddles/strangles, or credit spreads with the short strike at the wall). Don&apos;t chase breakouts.<br />
        • <strong>Candle closes THROUGH a green wall:</strong> the magnet is gone and dealer flow can flip —
        the next red wall / GEX-flip becomes the target; stop fading.<br />
        • <strong>Candle reaches / breaks a RED wall (or net gamma is negative):</strong> expect acceleration —
        trade WITH the break (debit options / spreads in the breakout direction), not against it. Selling
        premium here is dangerous (gamma works against you).<br />
        • <strong>Price near the GEX-flip line:</strong> this is the regime boundary — above it tends to be
        long-gamma (range), below it short-gamma (trend). Treat a flip-cross as a change of playbook.
      </P>
      <P>
        The live regime (<strong>LONG γ</strong> = range / sell-premium · <strong>SHORT γ</strong> =
        trend / debit), net GEX, GEX-flip-vs-spot, nearest walls, top OI S/R and PCR now live in the
        <strong> OverView panel</strong> (top-left, turn on OverView) — the old top-right box was removed
        to avoid duplication. All of the lines describe the one expiry chosen on the FNO chip. Rule of thumb: <strong>sell premium INTO green walls in a
        long-gamma regime; buy debit/spreads THROUGH red walls in a short-gamma regime.</strong>
      </P>
      <H3>Ranked levels, thickness, history vs latest, and the PCR line</H3>
      <P>
        • <strong>OI S/R and GEX walls are ranked by size</strong> (open-interest / |γ|): rank 1 is the
        single biggest and draws <strong>thickest</strong>; choose how many per side in Chart Settings →
        FNO. So "OI S/R = 1" plots only the biggest call-OI resistance + biggest put-OI support.<br />
        • <strong>Lines</strong> (Chart Settings → FNO): <em>Per candle</em> draws each level as it stood at
        every candle&apos;s close; <em>Latest only</em> draws a single flat line from the most recent value.<br />
        • <strong>PCR</strong> — put/call OI ratio in its own pane around 1.0;
        &gt;1 = put-heavy (supportive), &lt;1 = call-heavy (resistive).
      </P>
      <H3>The right-rail Profile — stacked lanes</H3>
      <P>
        Chart Settings → FNO → Profile = <strong>Combined</strong> shows stacked profiles sharing the
        price axis, with sophisticated gradient/opacity shading (no number clutter):
        <strong> OI</strong> (call/put walls) · <strong>ΔOI</strong> (NEW interest building green /
        unwinding red) · <strong>GEX</strong> (γ walls + the <strong>GEX-flip line</strong> across it) ·
        <strong> Fut</strong> (a <em>futures OI-by-price footprint</em> — where futures open interest built
        green / unwound red; fills as data accumulates). Single-metric modes (GEX / OI / ΔOI) also exist.
      </P>
      <H3>Crosshair FNO read + Bull/Bear rank — hover candle-by-candle</H3>
      <P>
        With FNO on (option symbols), <strong>hover the candles</strong> and the crosshair tooltip adds an
        <strong> FNO section</strong> read at that <strong>candle's close</strong> — so you can step bar by
        bar and test whether the option picture matched the price action. It interprets: GEX regime &amp;
        flip, nearest +γ/−γ walls, OI support/resistance, ΔOI build/unwind, Max-Pain pull, PCR, and the
        futures OI pattern — each a one-line <em>actionable outcome</em> + plain-English <em>explanation</em>.
        A <strong>Bull/Bear rank</strong> (−100…+100, colour-zoned) sits on top, blending every dimension
        into one score. The same score drives a third <strong>FNO speedometer</strong> in the OverView
        panel (at the live spot) next to the timeframe gauges.
      </P>

      <GuideExample title="A pre-trade FNO scan — NIFTY weekly expiry, Tuesday">
        Open Dashboard → click the NIFTY card (pulse score 78, up 12 from yesterday).<br />
        Overview tab: spot 22,150, ATM IV 13.5% (vs 30-day baseline 11.8%, red-tinted = elevated).
        Max-pain 22,000 (200 points below spot). Top suggested basket: Bear Put Spread 22,100/21,900.<br />
        Why-this-signal panel: "Heavy call-OI buildup at 22,200 + put-OI unwinding at 22,000 + GEX
        flipping negative below 22,100".<br />
        OI tab confirms — fresh 22,200 call wall, 22,000 put wall, ATM trapped between.<br />
        Read: market expects pin to max-pain (22,000) or downward drift; risk is a squeeze above 22,200.
        Bear Put Spread aligns. Click Execute Basket → OrderConfirmDialog Strategy tab → review legs (Buy
        22,100 PE / Sell 21,900 PE, qty 1 lot each), premium ₹2,800 net, max profit ₹12,200, max loss
        ₹2,800. Confirm.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Options Dashboard',  anchor: 'dashboard',     body: 'options dashboard pulse cards filters star high conviction' },
    { heading: 'Options Lab — 7 tabs', anchor: 'lab',         body: 'options lab seven tabs overview oi iv skew gex vwap chain futures' },
    { heading: 'Strategy Suggestions + basket', anchor: 'basket', body: 'strategy suggestions basket execution long straddle iron condor bull call spread' },
    { heading: 'Payoff curve',       anchor: 'payoff-curve',  body: 'payoff curve breakeven max profit max loss spot marker' },
    { heading: 'Greeks history',     anchor: 'greeks-history',body: 'greeks history delta gamma theta vega per strike snapshot' },
    { heading: 'FNO on the chart — expiry, lines, panes', anchor: 'fno-chart', body: 'fno chart indicator expiry switch d0 w0 m0 roll settlement pin date workspace pill call wall put wall max pain gamma flip pcr pane iv pane oi change pane net puts calls colours green red gaps collector shaded legend expected move straddle regime tint' },
    { heading: 'GEX walls — read & trade', anchor: 'gex-walls', body: 'gex walls gamma exposure chart green positive long gamma pin red negative short trend flip regime sell premium debit spread how to read interpret candle hits wall ranked thickness rank biggest oi historical latest mode pcr line profile lanes delta oi futures oi by price footprint build unwind crosshair hover tooltip candle close interpret actionable bull bear rank score speedometer meter overview panel gradient opacity' },
  ],
};

export default Section_fno_options;
